// alpha-ledger-page.js — Alpha Ledger subpage (#ledger under AI) + trust strip on the AI page.
// Decision Loop scoring phase, mockups v0.2 (content) + v0.3 (placement) approved 2026-07-21.
//
// Everything here is DERIVED: reads the local ledger (IDB) + live state, scores via the
// pure alpha-score core, renders. No save(), no cloudSave(), no storage of any kind.

import { S } from '../core/state.js';
import { esc, f, fU } from '../core/utils.js';
import { ledgerReadAll } from '../core/decision-ledger.js';
import { scoreChains, aggregateScores, fiDays, UNIT_NOTIONAL } from '../core/alpha-score.js';
import { forwardEssentialMonthlyTRY } from './cashflow.js';

const $ = id => document.getElementById(id);

function _ctx() {
  return { holdings: S.holdings || [], portfolios: S.portfolios || [], trades: S.trades || [], prices: S.prices || {}, fxRate: S.fxRate };
}

function _dailyEssentialUSD() {
  const fx = Number(S.fxRate);
  if (!Number.isFinite(fx) || fx <= 0) return null;
  return forwardEssentialMonthlyTRY() * 12 / 365 / fx;
}

const _fmtA = a => (a >= 0 ? '+' : '−') + '$' + f(Math.abs(a), Math.abs(a) < 100 ? 1 : 0);

/** Compute once per render pass; shared by strip + subpage. CHAIN mode (v0.2):
 *  superseded chains score as one thesis — entry at first call, exit at final resolution. */
async function _compute() {
  const events = await ledgerReadAll();
  const { rows, coverage } = scoreChains(events, _ctx());
  // Dismiss-reason distribution across ALL captured events (incl. mandate-filtered,
  // which never reach scored rows) — feeds the behavior-profile bars.
  const reasons = {};
  for (const e of events) {
    if (e.event === 'rec_dismissed') {
      const r = e.reasoning;
      const key = r === 'mandate_mismatch' ? 'mandate / policy'
        : r === 'later' ? 'later / not now'
        : r === 'disagree' ? 'disagree'
        : r === 'self_reported' ? 'self-reported'
        : r ? String(r).slice(0, 24)
        : '(no reason — pre-feature)';
      reasons[key] = (reasons[key] || 0) + 1;
    }
    // rec_expired is engine TTL lifecycle (scan replacement), not a user decision —
    // 195 of them would drown the real behavior signal, so bars show dismissals only.
  }
  return { agg: aggregateScores(rows), coverage, eventCount: events.length, rows, reasons };
}

/** One-line trust strip on the AI page (always visible once data exists). */
export function renderTrustStrip() {
  const strip = $('alphaStrip');
  if (!strip) return;
  _compute().then(({ agg, coverage }) => {
    if (!agg.n) { strip.style.display = 'none'; return; }
    strip.style.display = '';
    const saved = -agg.total; // negative advice alpha = ignoring saved money
    // Same sign frame as the hero below: magnitude + the word. The strip used to take
    // Math.abs while the hero printed the signed value, so the SAME quantity appeared as
    // +$3,430 on one surface and −$3,430 on the other. (audit 2026-08-01)
    const hitTxt = agg.hitPctResolved != null
      ? `hit <b>${agg.hitPctResolved}%</b> <span class="al-sub">of ${agg.resolved} resolved</span>`
      : `<span class="al-sub">no resolved theses yet</span>`;
    $('asMain').innerHTML = `ignoring ${saved >= 0 ? 'saved' : 'cost'} <b class="${saved >= 0 ? 'pos' : 'neg'}">${_fmtA(Math.abs(saved))}</b> <span class="al-sub">per $${UNIT_NOTIONAL / 1000}k / thesis</span> · ${hitTxt} · ${agg.n} scored`;
    const sl = agg.byType.stop_loss, tp = agg.byType.take_profit;
    // "of which": SL and TP are SUBSETS of the headline, not additions to it. Printed bare
    // they read as if they sum with it, and they can exceed it.
    $('asTypes').innerHTML = (sl || tp) ? 'of which ' + [
      sl ? `SL <b class="${sl.total >= 0 ? 'pos' : 'neg'}">${_fmtA(sl.total)}</b>` : '',
      tp ? `TP <b class="${tp.total >= 0 ? 'pos' : 'neg'}">${_fmtA(tp.total)}</b>` : '',
    ].filter(Boolean).join(' · ') : '';
    $('asFiltered').innerHTML = coverage.mandate_filtered ? `mandate-filtered <b class="gold">${coverage.mandate_filtered}</b>` : '';
  }).catch(e => console.warn('[ALPHA] strip failed:', e && e.message));
}

/** Full subpage render. */
export function renderLedgerPage() {
  if (!$('alHeroNum')) return;
  _compute().then(({ agg, coverage, eventCount, rows, reasons }) => {
    const meta = $('ledgerMeta');
    if (meta) meta.innerHTML = `<b>${eventCount}</b> ledger events · ${coverage.chained_legs || agg.n} legs → ${agg.n} theses · ${agg.resolved} resolved · client-side, zero cloud`;

    const saved = -agg.total;
    const hn = $('alHeroNum');
    hn.className = 'neo-hnum ' + (saved > 0 ? 'pos' : saved < 0 ? 'neg' : '');
    hn.innerHTML = agg.n
      ? `${_fmtA(Math.abs(saved))} <small style="font-size:15px;color:var(--t2);font-weight:500">${saved >= 0 ? 'saved by ignoring' : 'cost of ignoring'} · per $${UNIT_NOTIONAL / 1000}k per thesis${Math.abs(saved) < 150 ? ' · even money so far' : ''}</small>`
      : 'No scoreable recs yet';
    $('alHeroChips').innerHTML =
      `<span class="neo-chip" title="One chain = one thesis: superseded recs linked from first call to final resolution">Theses <b class="hx">${agg.n}</b> · ${agg.resolved} resolved${agg.avgLegs > 1 ? ` · ${agg.avgLegs} legs avg` : ''}</span>` +
      `<span class="neo-chip" title="Share of RESOLVED theses that ended positive. Open theses are marked to live price and cannot be a hit or a miss yet.">Hit-rate <b>${agg.hitPctResolved != null ? agg.hitPctResolved + '%' : '—'}</b>${agg.hitPctResolved != null ? ` <span class="al-sub">of ${agg.resolved}</span>` : ''}</span>` +
      // Counted the same way as the Followed vs Ignored section below: theses whose
      // terminal event is rec_acted. coverage.acted counts EVENTS, so the chip and the
      // section used to disagree on one page. (audit 2026-08-01)
      `<span class="neo-chip">Followed <b>${(rows || []).filter(r => r.closeEvent === 'rec_acted').length}</b> <span class="al-sub">theses</span></span>` +
      `<span class="neo-chip">Mandate-filtered <b class="gold">${coverage.mandate_filtered}</b></span>` +
      (agg.medianLifeDays != null ? `<span class="neo-chip">Median thesis life <b>${agg.medianLifeDays}d</b></span>` : '');
    const sl = agg.byType.stop_loss, tp = agg.byType.take_profit;
    $('alHeroCtx').innerHTML = agg.n
      ? `In the active lane (Trades + Crypto + SAP) the engine and your inaction are ${Math.abs(agg.total) < 150 ? '<b>roughly even</b>' : agg.total > 0 ? '<b class="neg">behind — its calls carried value</b>' : '<b class="pos">ahead — its calls would have cost</b>'}.` +
        (sl && tp ? ` Stop-loss calls ${sl.total >= 0 ? 'carry value' : 'lag'} (<b class="${sl.total >= 0 ? 'pos' : 'neg'}">${_fmtA(sl.total)}, ${sl.hitPct}% hit</b>); take-profits ${tp.total >= 0 ? 'deliver' : 'fire early'} (<b class="${tp.total >= 0 ? 'pos' : 'neg'}">${_fmtA(tp.total)}, ${tp.hitPct}%</b>).` : '')
      : 'Directive recommendations in the active lane will appear here as they resolve.';

    const daily = _dailyEssentialUSD();
    const fd = daily ? fiDays(saved, daily) : null;
    $('alFiDays').textContent = fd != null ? (fd >= 0 ? '+' : '−') + Math.abs(fd) : '—';

    // Finding card — engine/mandate awareness (until the source fix ships)
    const ins = $('alInsight');
    if (ins) {
      ins.innerHTML = coverage.mandate_filtered
        ? `<div class="al-insight"><div class="l">⚠ FINDING — THE ENGINE DOES NOT KNOW YOUR MANDATE YET (SOURCE FIX QUEUED)</div>
           <div class="s"><b>${coverage.mandate_filtered}</b> directives targeted accumulate-mandate (Retirement) holdings where selling is policy-forbidden.
           They are counted here and excluded from scoring. The rec engine will emit DCA-shift / DCA-opportunity for these assets instead of sell / take-profit.</div></div>`
        : '';
    }

    // Report card table
    const order = Object.entries(agg.byType).sort((a, b) => b[1].n - a[1].n);
    $('alTblBody').innerHTML = order.map(([ty, a]) =>
      `<tr><td><span class="al-tybdg">${esc(ty)}</span>${a.n < 5 ? ' <span style="font-family:var(--mono);font-size:9px;color:var(--t3)">low n</span>' : ''}</td>` +
      `<td class="tr">${a.n}</td><td class="tr">${a.hitPct}%</td>` +
      `<td class="tr ${a.avg >= 0 ? 'pos' : 'neg'}">${_fmtA(a.avg)}</td>` +
      `<td class="tr ${a.total >= 0 ? 'pos' : 'neg'}">${_fmtA(a.total)}</td></tr>`
    ).join('') || '<tr><td colspan="5" class="neo-empty">No scored recommendations yet</td></tr>';

    // Best / worst call cards
    const call = (el, r, label, better) => {
      if (!el) return;
      el.innerHTML = r
        ? `<div class="l">${label}</div><div class="v ${r.alpha >= 0 ? 'pos' : 'neg'}">${esc(r.ticker)} · ${esc(r.type)} · ${_fmtA(r.alpha)}</div>` +
          `<div class="s">${esc((r.reasoning || '').slice(0, 140)) || (better ? 'Following this call would have earned.' : 'Following this call would have cost.')}</div>`
        : `<div class="l">${label}</div><div class="v">—</div>`;
    };
    call($('alBest'), agg.best, 'BEST CALL · FOLLOWING WOULD HAVE EARNED', true);
    call($('alWorst'), agg.worst, 'WORST CALL · FOLLOWING WOULD HAVE COST', false);

    // Coverage chips (honesty panel)
    // Units are named because emitted/acted/dismissed count EVENTS while
    // scored/mandate-filtered count CHAINS (theses). Dividing one by the other was
    // apples-to-oranges and the panel gave no way to know. (audit 2026-08-01)
    const cov = [
      ['emitted (legs)', coverage.emitted], ['scored (theses)', coverage.scored],
      ['mandate-filtered (theses)', coverage.mandate_filtered, 'hot'], ['no price (theses)', coverage.no_price],
      ['rebalance (theses)', coverage.rebalance], ['alerts / holds (theses)', coverage.non_directive],
      ['acted (legs)', coverage.acted], ['dismissed (legs)', coverage.dismissed],
    ];
    $('alCoverage').innerHTML = cov.map(([l, v, hot]) =>
      `<span class="al-cov${hot ? ' hot' : ''}">${l} <b>${v}</b></span>`).join('') +
      (coverage.acted === 0 ? '<span class="al-cov">act on a rec to unlock followed-vs-ignored</span>' : '');

    _renderFollowedIgnored(rows || [], reasons || {}, coverage);
  }).catch(e => console.warn('[ALPHA] page render failed:', e && e.message));
}

/** Followed vs Ignored section (mockup v0.2 approved 2026-07-24). Same $1k counterfactual:
 *  Followed = theses whose terminal event is rec_acted (alpha ≈ value of following, realized
 *  in counterfactual terms); Ignored = everything else scored. Mandate-filtered stay excluded
 *  from α by design but ARE counted in the reason bars. */
function _renderFollowedIgnored(rows, reasons, coverage) {
  if (!$('fiFolTot')) return;
  const followed = rows.filter(r => r.closeEvent === 'rec_acted');
  const ignored = rows.filter(r => r.closeEvent !== 'rec_acted');
  const fSum = followed.reduce((s, r) => s + r.alpha, 0);
  const iSum = ignored.reduce((s, r) => s + r.alpha, 0);

  const whyOf = r => {
    if (r.closeEvent === 'rec_acted') {
      const n = r.closeNote || '';
      if (n === 'self_reported') return 'via <b>I did this</b> (self-reported)';
      return n ? `via <b>${esc(n.slice(0, 42))}</b>` : 'via <b>acted</b>';
    }
    if (r.closeEvent === 'rec_dismissed') {
      const n = r.closeNote;
      return `reason: <b>${n === 'later' ? 'later / not now' : n === 'disagree' ? 'disagree' : n ? esc(String(n).slice(0, 28)) : '(no reason — pre-feature)'}</b>`;
    }
    if (r.closeEvent === 'rec_expired') return 'reason: <b>(expired unseen)</b>';
    if (r.closeEvent === 'rec_superseded') return 'superseded — thesis replaced';
    return '<b>open</b> — still marked to market';
  };
  const rowHtml = r =>
    `<div class="fi2-row"><span class="fi2-tk">${esc(r.ticker)}</span><span class="al-tybdg">${esc(r.type)}</span>` +
    `<span class="fi2-why">${whyOf(r)}</span>` +
    `<span class="fi2-a ${r.resolved || r.closeEvent ? (r.alpha >= 0 ? 'pos' : 'neg') : ''}" ${!r.resolved && !r.closeEvent ? 'style="color:var(--t3)"' : ''}>${!r.resolved && !r.closeEvent ? 'open' : _fmtA(r.alpha)}</span></div>`;
  // Display list: reasoned closures first (they carry the story), then by |alpha|;
  // one row per ticker so four open ALCAR chains don't fill the whole column.
  const top = arr => {
    const sorted = arr.slice().sort((a, b) =>
      ((b.closeEvent ? 1 : 0) - (a.closeEvent ? 1 : 0)) || (Math.abs(b.alpha) - Math.abs(a.alpha)));
    const seen = new Set(); const out = [];
    for (const r of sorted) { const k = r.ticker + '|' + r.type; if (seen.has(k)) continue; seen.add(k); out.push(r); if (out.length === 4) break; }
    return out;
  };

  const fTot = $('fiFolTot');
  fTot.className = 'fi2-big ' + (followed.length ? (fSum >= 0 ? 'pos' : 'neg') : '');
  fTot.textContent = followed.length ? _fmtA(fSum) : '—';
  const fHits = followed.filter(r => r.alpha > 0).length;
  $('fiFolSub').innerHTML = followed.length
    ? `${followed.length} theses acted on · ${Math.round(fHits / followed.length * 100)}% hit · following was worth <b class="${fSum >= 0 ? 'pos' : 'neg'}">${_fmtA(fSum)}</b>`
    : 'Act on a rec to populate — <b>✓ I did this</b> counts.';
  $('fiFolRows').innerHTML = top(followed).map(rowHtml).join('');

  const iTot = $('fiIgnTot');
  const iDisp = -iSum; // mirror of the hero: positive alpha ignored = cost
  iTot.className = 'fi2-big ' + (ignored.length ? (iDisp >= 0 ? 'pos' : 'neg') : '');
  iTot.textContent = ignored.length ? _fmtA(iDisp) : '—';
  $('fiIgnSub').innerHTML = ignored.length
    ? `${ignored.length} theses unfollowed · ignoring ${iSum >= 0 ? 'cost' : 'saved'} <b class="${iSum >= 0 ? 'neg' : 'pos'}">${_fmtA(Math.abs(iSum) * (iSum >= 0 ? -1 : 1))}</b>` +
      (coverage.mandate_filtered ? ` · <span class="gold">+${coverage.mandate_filtered} mandate-policy excluded from α</span>` : '')
    : 'No unfollowed theses yet.';
  $('fiIgnRows').innerHTML = top(ignored).map(rowHtml).join('');

  // Behavior bars — need 3+ captured reasons (pre-feature nulls don't count toward the gate)
  const captured = Object.entries(reasons).filter(([k]) => k !== '(no reason — pre-feature)');
  const capturedN = captured.reduce((s, [, v]) => s + v, 0);
  const barsEl = $('fiBars');
  if (barsEl) {
    if (capturedN >= 3) {
      barsEl.style.display = '';
      const COLB = { 'mandate / policy': 'var(--gold)', 'later / not now': 'var(--cyan)', 'disagree': 'var(--red)', 'expired unseen': 'var(--t3)' };
      const mx = Math.max(...captured.map(([, v]) => v), 1);
      $('fiBarRows').innerHTML = captured.sort((a, b) => b[1] - a[1]).map(([k, v]) =>
        `<div class="fi2-bar"><span class="fi2-bn">${esc(k)}</span>` +
        `<div class="fi2-bt"><div class="fi2-bf" style="width:${Math.round(v / mx * 100)}%;background:${COLB[k] || 'var(--vio)'}"></div></div>` +
        `<span class="fi2-bv">${v}</span></div>`).join('');
    } else barsEl.style.display = 'none';
  }

  // Verdict — only with 5+ captured reasons AND at least one costly ignored group
  const vEl = $('fiVerdict');
  if (vEl) {
    if (capturedN >= 5 && ignored.length) {
      // Verdict judges USER decisions only (dismiss reasons) — engine TTL expiries are
      // excluded here for the same reason they are excluded from the bars.
      const byReason = {};
      ignored.forEach(r => { if (r.closeEvent !== 'rec_dismissed') return;
        const k = r.closeNote === 'later' ? 'later / not now' : r.closeNote === 'disagree' ? 'disagree' : null;
        if (!k) return; if (!byReason[k]) byReason[k] = 0; if (r.alpha > 0) byReason[k] += r.alpha; });
      const worst = Object.entries(byReason).sort((a, b) => b[1] - a[1])[0];
      if (worst && worst[1] > 25) {
        vEl.style.display = '';
        vEl.innerHTML = `Early read: your <b>"${esc(worst[0])}"</b> dismissals carry the largest cost (${_fmtA(-worst[1])} of the ignored total) — ` +
          `${worst[0] === 'disagree' ? 'active disagreement' : 'deferral, not disagreement,'} is the expensive habit. Mandate dismissals are policy working as designed and cost nothing.`;
      } else vEl.style.display = 'none';
    } else vEl.style.display = 'none';
  }
}

/** Header/strip action → open the subpage (hash nav so browser Back works). */
export function openLedger() {
  location.hash = '#ledger';
}
